/**
 * SFTP SSH key UI state (Story 2.3 — masked display vs replace / entry).
 * Pure helpers for unit tests and AddWorkflowStepPopup.
 */

export type SftpPrivateKeyMaskUiParams = {
    isSftpSshKeyAuth: boolean;
    isEdit: boolean;
    isClone: boolean;
    secretValue: Record<string, unknown> | null | undefined;
    replaceMode: boolean;
    useTemplateVariables: boolean;
    /** Top-level `formData.privateKey` (may hold PEM or Secrets Manager reference). */
    privateKey: string;
};

function secretValueKeyNonEmpty(
    sv: Record<string, unknown> | null | undefined,
    key: string
): boolean {
    if (!sv || typeof sv !== 'object' || !(key in sv)) {
        return false;
    }
    const v = sv[key];
    return v != null && String(v).trim() !== '';
}

/**
 * Legacy `*****` textarea mask (same predicate as AddWorkflowStepPopup before Story 2.3).
 */
function legacyEditUsesMaskedPrivateKeyField(p: Omit<SftpPrivateKeyMaskUiParams, 'privateKey'>): boolean {
    if (!p.isEdit) {
        return false;
    }
    const sv = p.secretValue;
    const cloneWithKeySlot =
        !!p.isClone &&
        !!sv &&
        typeof sv === 'object' &&
        ('privateKey' in sv || 'SshPrivateKeyReference' in sv || 'sshPrivateKeyReference' in sv);
    return !cloneWithKeySlot;
}

/**
 * When true, show the "key stored on server" summary + Replace key instead of the PEM textarea.
 * Requires that a key or reference is actually present so clone-without-key still shows the entry UI.
 */
export function isSftpPrivateKeyMaskedSummaryUi(p: SftpPrivateKeyMaskUiParams): boolean {
    if (!p.isSftpSshKeyAuth || p.useTemplateVariables || p.replaceMode) {
        return false;
    }
    // Clone wizard copies step shape (and may carry refs); the new workflow has no saved secret yet — always show key entry, not "stored (hidden)".
    if (p.isClone) {
        return false;
    }
    if (!legacyEditUsesMaskedPrivateKeyField(p)) {
        return false;
    }
    const pk = (p.privateKey ?? '').trim();
    if (pk.length > 0) {
        return true;
    }
    const sv = p.secretValue;
    return (
        secretValueKeyNonEmpty(sv, 'privateKey') ||
        secretValueKeyNonEmpty(sv, 'SshPrivateKeyReference') ||
        secretValueKeyNonEmpty(sv, 'sshPrivateKeyReference')
    );
}

export type SftpPrivateKeyTextareaMaskParams = {
    isEdit: boolean;
    isClone: boolean;
    secretValue: Record<string, unknown> | null | undefined;
    replaceMode: boolean;
    privateKey: string | null | undefined;
};

/**
 * When the non-template SSH private key textarea should bind to `*****` instead of `formData.privateKey`.
 * Unmasks when the user has pasted/uploaded new material or is in Replace-key entry mode.
 */
export function editModeUsesMaskedSftpPrivateKeyTextarea(p: SftpPrivateKeyTextareaMaskParams): boolean {
    if (!p.isEdit) {
        return false;
    }
    if (p.isClone) {
        return false;
    }
    if (p.replaceMode) {
        return false;
    }
    const sv = p.secretValue;
    const cloneWithKeySlot =
        !!p.isClone &&
        !!sv &&
        typeof sv === 'object' &&
        ('privateKey' in sv || 'SshPrivateKeyReference' in sv || 'sshPrivateKeyReference' in sv);
    if (cloneWithKeySlot) {
        return false;
    }
    const pk = String(p.privateKey ?? '').trim();
    if (pk !== '' && pk !== '*****') {
        return false;
    }
    return true;
}
